import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Button } from '@mui/material';
import {
  Chat,
  ChevronRight,
  Person,
  SmartToy,
  Psychology,
  CheckCircle,
  Cancel,
  RadioButtonUnchecked,
  RadioButtonChecked,
} from '@mui/icons-material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { useGetStreamingThreadDetailQuery } from '../store/api';
import type { StreamingThreadMessage } from '../store/api';
import { formatDateTime } from '../utils/formatting';

// Types for processed timeline items
interface MultipleChoiceOption {
  id: string;
  label: string;
  description?: string | null;
}

interface MultipleChoiceInterrupt {
  question: string;
  options: MultipleChoiceOption[];
  allowTextInput: boolean;
  textInputPlaceholder?: string;
  allowMultiple: boolean;
}

interface ConfirmationInterrupt {
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  confirmationType?: string;
}

interface TimelineItem {
  type: 'user_message' | 'thinking' | 'section_content' | 'summary' | 'multiple_choice' | 'confirmation';
  timestamp: string;
  // User message
  userMessage?: string;
  // Thinking
  thinkingMessage?: string;
  // Section content
  namespace?: string;
  sectionContent?: string;
  // Summary
  summaryContent?: string;
  // Multiple choice
  multipleChoice?: MultipleChoiceInterrupt;
  selectedIds?: string[];
  textInput?: string;
  // Confirmation
  confirmation?: ConfirmationInterrupt;
  confirmed?: boolean;
}

// Process messages into a timeline
function processMessagesToTimeline(messages: StreamingThreadMessage[]): TimelineItem[] {
  const timeline: TimelineItem[] = [];
  const pendingInterrupts = new Map<string, { type: string; payload: Record<string, unknown>; timestamp: string }>();

  for (const msg of messages) {
    const payload = msg.payload;

    // User messages from SESSION_CREATED
    if (msg.event_type === 'SESSION_CREATED') {
      if (payload.user_message) {
        timeline.push({
          type: 'user_message',
          timestamp: msg.created_at,
          userMessage: String(payload.user_message),
        });
      }

      // Handle resume (response to interrupt)
      if (payload.resume) {
        const resume = payload.resume as { interrupt_id: string; resume_payload: Record<string, unknown> };
        const interrupt = pendingInterrupts.get(resume.interrupt_id);

        if (interrupt) {
          if (interrupt.type === 'MULTIPLE_CHOICE') {
            const interruptPayload = interrupt.payload as unknown as MultipleChoiceInterrupt & { eventType: string };
            timeline.push({
              type: 'multiple_choice',
              timestamp: msg.created_at,
              multipleChoice: {
                question: interruptPayload.question,
                options: interruptPayload.options || [],
                allowTextInput: interruptPayload.allowTextInput,
                textInputPlaceholder: interruptPayload.textInputPlaceholder,
                allowMultiple: interruptPayload.allowMultiple,
              },
              selectedIds: (resume.resume_payload.selected_ids as string[]) || [],
              textInput: resume.resume_payload.text_input as string | undefined,
            });
          } else if (interrupt.type === 'CONFIRMATION') {
            const interruptPayload = interrupt.payload as unknown as ConfirmationInterrupt & { eventType: string };
            timeline.push({
              type: 'confirmation',
              timestamp: msg.created_at,
              confirmation: {
                message: interruptPayload.message,
                confirmLabel: interruptPayload.confirmLabel,
                cancelLabel: interruptPayload.cancelLabel,
                confirmationType: interruptPayload.confirmationType,
              },
              confirmed: resume.resume_payload.confirmed as boolean,
            });
          }
          pendingInterrupts.delete(resume.interrupt_id);
        }
      }
    }

    // Thinking activity
    if (msg.event_type === 'ACTIVITY_SNAPSHOT' && payload.activityType === 'THINKING') {
      const content = payload.content as { message?: string } | undefined;
      if (content?.message) {
        // Replace previous thinking message if exists
        const lastItem = timeline[timeline.length - 1];
        if (lastItem?.type === 'thinking') {
          lastItem.thinkingMessage = content.message;
          lastItem.timestamp = msg.created_at;
        } else {
          timeline.push({
            type: 'thinking',
            timestamp: msg.created_at,
            thinkingMessage: content.message,
          });
        }
      }
    }

    // Section content
    if (msg.event_type === 'TEXT_MESSAGE_END' && payload.fullContent) {
      const namespace = payload.namespace as string | undefined;
      if (namespace?.startsWith('section:')) {
        timeline.push({
          type: 'section_content',
          timestamp: msg.created_at,
          namespace: namespace.replace('section:', ''),
          sectionContent: String(payload.fullContent),
        });
      } else if (namespace === 'assistant') {
        timeline.push({
          type: 'summary',
          timestamp: msg.created_at,
          summaryContent: String(payload.fullContent),
        });
      }
    }

    // RUN_FINISHED with interrupt - store for matching with response
    if (msg.event_type === 'RUN_FINISHED' && payload.interrupt) {
      const interrupt = payload.interrupt as { id: string; payload: Record<string, unknown> & { eventType: string }; reason: string };
      pendingInterrupts.set(interrupt.id, {
        type: interrupt.payload.eventType,
        payload: interrupt.payload,
        timestamp: msg.created_at,
      });
    }
  }

  return timeline;
}

// Component for user messages
function UserMessageCard({ item }: { item: TimelineItem }) {
  return (
    <Paper className="p-4 mb-3 border-l-4 border-l-blue-500" elevation={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Person className="h-5 w-5 text-blue-600" />
        <Typography variant="subtitle2" color="primary">
          User
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {formatDateTime(item.timestamp)}
        </Typography>
      </Box>
      <Typography variant="body1">{item.userMessage}</Typography>
    </Paper>
  );
}

// Component for thinking
function ThinkingCard({ item }: { item: TimelineItem }) {
  return (
    <Paper className="p-3 mb-3 bg-amber-50" elevation={0}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Psychology className="h-4 w-4 text-amber-600" />
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {item.thinkingMessage}
        </Typography>
      </Box>
    </Paper>
  );
}

// Component for section content
function SectionContentCard({ item }: { item: TimelineItem }) {
  return (
    <Paper className="p-4 mb-3 border-l-4 border-l-green-500" elevation={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <SmartToy className="h-5 w-5 text-green-600" />
        <Typography variant="subtitle2" color="success.main">
          Section Update
        </Typography>
        {item.namespace && (
          <Chip label={item.namespace} size="small" variant="outlined" />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {formatDateTime(item.timestamp)}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          backgroundColor: 'grey.50',
          p: 2,
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.85rem',
        }}
      >
        {item.sectionContent}
      </Typography>
    </Paper>
  );
}

// Component for summary
function SummaryCard({ item }: { item: TimelineItem }) {
  return (
    <Paper className="p-3 mb-3 bg-green-50" elevation={0}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {item.summaryContent}
        </Typography>
      </Box>
    </Paper>
  );
}

// Component for multiple choice interaction
function MultipleChoiceCard({ item }: { item: TimelineItem }) {
  const { multipleChoice, selectedIds = [], textInput } = item;
  if (!multipleChoice) return null;

  return (
    <Paper className="p-4 mb-3 border-l-4 border-l-purple-500" elevation={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Chat className="h-5 w-5 text-purple-600" />
        <Typography variant="subtitle2" color="secondary">
          User Selection
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {formatDateTime(item.timestamp)}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ fontWeight: "medium", mb: 2 }}>
        {multipleChoice.question}
      </Typography>

      <Box sx={{ pl: 1 }}>
        {multipleChoice.options.map((option) => {
          const isSelected = selectedIds.includes(option.id);
          return (
            <Box
              key={option.id}
              sx={{display: "flex", alignItems: "center", gap: 1,
                py: 0.5,
                color: isSelected ? 'primary.main' : 'text.disabled',
              }}
            >
              {isSelected ? (
                <RadioButtonChecked className="h-4 w-4" color="primary" />
              ) : (
                <RadioButtonUnchecked className="h-4 w-4" color="disabled" />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 400,
                  textDecoration: isSelected ? 'none' : 'line-through',
                  opacity: isSelected ? 1 : 0.5,
                }}
              >
                {option.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {textInput && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: "grey.100", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Custom input:
          </Typography>
          <Typography variant="body2">{textInput}</Typography>
        </Box>
      )}
    </Paper>
  );
}

// Component for confirmation interaction
function ConfirmationCard({ item }: { item: TimelineItem }) {
  const { confirmation, confirmed } = item;
  if (!confirmation) return null;

  return (
    <Paper className="p-4 mb-3 border-l-4 border-l-purple-500" elevation={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Chat className="h-5 w-5 text-purple-600" />
        <Typography variant="subtitle2" color="secondary">
          User Confirmation
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {formatDateTime(item.timestamp)}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 2 }}>
        {confirmation.message}
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Chip
          icon={confirmed ? undefined : <Cancel fontSize="small" />}
          label={confirmation.cancelLabel}
          size="small"
          variant={confirmed ? 'outlined' : 'filled'}
          color={confirmed ? 'default' : 'error'}
          sx={{ opacity: confirmed ? 0.5 : 1 }}
        />
        <Chip
          icon={confirmed ? <CheckCircle fontSize="small" /> : undefined}
          label={confirmation.confirmLabel}
          size="small"
          variant={confirmed ? 'filled' : 'outlined'}
          color={confirmed ? 'success' : 'default'}
          sx={{ opacity: confirmed ? 1 : 0.5 }}
        />
      </Box>
    </Paper>
  );
}

// Render a timeline item
function TimelineItemCard({ item }: { item: TimelineItem }) {
  switch (item.type) {
    case 'user_message':
      return <UserMessageCard item={item} />;
    case 'thinking':
      return <ThinkingCard item={item} />;
    case 'section_content':
      return <SectionContentCard item={item} />;
    case 'summary':
      return <SummaryCard item={item} />;
    case 'multiple_choice':
      return <MultipleChoiceCard item={item} />;
    case 'confirmation':
      return <ConfirmationCard item={item} />;
    default:
      return null;
  }
}

export default function StreamingThreadDetail() {
  const { threadId } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();
  const referrerUserId = searchParams.get('userId');

  const listUrl = referrerUserId
    ? `/admin-v2/provider/${encodeURIComponent(referrerUserId)}/streaming-threads`
    : '/admin-v2/streaming-threads';

  const { data, isLoading } = useGetStreamingThreadDetailQuery(
    { threadId: threadId || '' },
    { skip: !threadId },
  );

  const timeline = useMemo(() => {
    if (!data?.messages) return [];
    return processMessagesToTimeline(data.messages);
  }, [data?.messages]);

  if (!threadId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <Typography variant="h5" color="text.primary" sx={{ fontWeight: "bold" }}>
              Invalid URL parameters
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Missing thread ID
            </Typography>
            <Link to={listUrl}>
              <Button className="mt-4">Back to Threads</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading thread details..." />;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <Typography variant="h5" color="text.primary" sx={{ fontWeight: "bold" }}>
              Thread not found
            </Typography>
            <Link to={listUrl}>
              <Button className="mt-4">Back to Threads</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl={listUrl} />
        </div>

        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <Link to="/admin-v2" className="text-gray-500 hover:text-gray-700">
                Admin
              </Link>
            </li>
            <ChevronRight className="h-5 w-5 text-gray-400" />
            {referrerUserId && (
              <>
                <li>
                  <Link
                    to={`/admin-v2/provider/${encodeURIComponent(referrerUserId)}`}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {data?.user_name || 'Provider'}
                  </Link>
                </li>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </>
            )}
            <li>
              <Link to={listUrl} className="text-gray-500 hover:text-gray-700">
                Streaming Threads
              </Link>
            </li>
            <ChevronRight className="h-5 w-5 text-gray-400" />
            <li>
              <span className="text-gray-900">Thread Details</span>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <Paper className="p-6 mb-6">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Chat className="h-6 w-6 text-blue-600" />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Thread {threadId.substring(0, 8)}...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.total_events} events • {timeline.length} timeline items
              </Typography>
            </Box>
          </Box>

          {/* Linked Resources */}
          <Box
            sx={{display: "flex", flexWrap: "wrap", gap: 3,
              pt: 3,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {data.note_id && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Note ID
                </Typography>
                <Link
                  to={`/admin-v2/note/${data.note_id}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  {data.note_id}
                </Link>
              </Box>
            )}
            {data.encounter_id && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Encounter ID
                </Typography>
                <Link
                  to={`/admin-v2/encounter/${data.encounter_id}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  {data.encounter_id}
                </Link>
              </Box>
            )}
            {data.template_id && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Template
                </Typography>
                {data.template_id.startsWith('p:') || data.template_id.startsWith('u:') ? (
                  <Typography variant="body2" sx={{fontFamily: "monospace",  fontWeight: "medium" }}>
                    {data.template_name || data.template_id}
                  </Typography>
                ) : (
                  <Link
                    to={`/admin-v2/templates/${data.template_id}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {data.template_name || data.template_id}
                  </Link>
                )}
              </Box>
            )}
            {data.user_id && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  User
                </Typography>
                <Link
                  to={`/admin-v2/provider/${data.user_id}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  {data.user_name || data.user_id}
                </Link>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Timeline */}
        <Typography variant="h6" sx={{ fontWeight: "medium", mb: 2 }}>
          Conversation Timeline
        </Typography>

        {timeline.length > 0 ? (
          <Box>
            {timeline.map((item, index) => (
              <TimelineItemCard key={index} item={item} />
            ))}
          </Box>
        ) : (
          <Paper className="p-8 text-center">
            <Typography color="text.secondary">No messages in this thread</Typography>
          </Paper>
        )}
      </main>
    </div>
  );
}
